import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangleIcon, ZapOffIcon, EyeOffIcon, FrownIcon, LightbulbOffIcon, TrendingDownIcon, ConstructionIcon, ScaleIcon, DollarSignIcon } from 'lucide-react';
export function ProblemSection() {
  const problems = [{
    icon: '❌',
    title: 'Tus mesas no quedan como imaginabas',
    desc: 'Aunque seguís tutoriales, el resultado nunca se ve profesional.'
  }, {
    icon: '💸',
    title: 'Gastás dinero en materiales innecesarios',
    desc: 'Comprás resina, pigmentos y herramientas que después no terminás usando.'
  }, {
    icon: '😩',
    title: 'No sabés por dónde empezar',
    desc: 'Encontrás información distinta en todos lados y terminás más confundido.'
  }, {
    icon: '🫧',
    title: 'Las burbujas arruinan tus proyectos',
    desc: 'Después de horas de trabajo, aparecen imperfecciones difíciles de corregir.'
  }, {
    icon: '⏳',
    title: 'Perdés tiempo con prueba y error',
    desc: 'Cada intento fallido significa más tiempo y más dinero perdido.'
  }];
  return <section className="py-16 bg-[#fdf8f3] text-gray-900 relative z-10 overflow-hidden pt-[30px] pb-[30px]" data-id="element-233" data-mp="ocztwtw3sfa5">
      {/* Chaotic storm textures - blurred distorted shapes */}
      <div className="absolute inset-0 z-0 pointer-events-none opacity-30 overflow-hidden" data-id="element-234" data-mp="x4ry4mujas6a">
        <div className="absolute top-10 left-10 w-96 h-96 bg-red-900/20 rounded-full blur-[120px]" data-id="element-235" data-mp="1244ld8pbi19e" />
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-gray-800/30 rounded-full blur-[100px]" data-id="element-236" data-mp="1726c8fszj4vf" />
        <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-red-800/15 blur-[80px] skew-x-12" data-id="element-237" data-mp="1c1iz30juwby0" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMDIpIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-20" data-id="element-238" data-mp="1g3nr5uunp0ij" />
      </div>

      <div className="max-w-5xl mx-auto px-4 relative z-10" data-id="element-239" data-mp="y1qen2osuy02">
        <div className="text-center mb-8" data-id="element-240" data-mp="tzlkm2ufvzcw">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-4xl md:text-5xl lg:text-6xl font-black mb-3 tracking-tight text-[#92400e] leading-[1.05] uppercase" data-id="element-241" data-mp="txsdynjttf4w">
            EL PROBLEMA REAL
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-xl text-gray-600 max-w-2xl mx-auto font-medium leading-relaxed" data-id="element-242" data-mp="1qpct55zkfn1p">
            Si te identificás con alguno de estos problemas, este ebook es para
            vos. Dejá de arruinar piezas y empezá a crear con maestría.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-243" data-mp="1mlbprj48ydf8">
          {problems.map((problem, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} className="relative group" data-id="element-244" data-mp="1rdbstkfm5mg1" data-mpi={index}>
              <div className="absolute inset-0 bg-gradient-to-b from-[#d97706]/5 to-transparent rounded-2xl -z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-245" data-mp="10lcyczdpa1ri" />
              <div className="p-6 md:p-8 border border-gray-200 rounded-2xl bg-white/80 backdrop-blur-sm hover:border-[#d97706]/30 transition-colors duration-300 h-full shadow-sm" data-id="element-246" data-mp="vocr4l546syt">
                <div className="bg-[#fdf8f3] w-14 h-14 rounded-xl flex items-center justify-center shadow-inner mb-6 border border-[#d97706]/10 group-hover:scale-110 transition-transform duration-300" data-id="element-247" data-mp="mtprk5m4tauh">
                  <span className="text-3xl" data-id="element-248" data-mp="cuh13mqh6ecv">{problem.icon}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 tracking-wide" data-id="element-249" data-mp="7x7d8biqiar5">
                  {problem.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm font-medium" data-id="element-250" data-mp="22aq5p6xwr35l">
                  {problem.desc}
                </p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}